import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import { articles, categories } from '@/mocks/home';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  strategy: {
    title: '征途国际游戏攻略 - 新手入门到高手进阶 | 征途国际官方下载',
    description: '征途国际最新游戏攻略大全：新手入门、职业选择、装备打造、国战战术、帮会联赛等全方位攻略指南，助你快速成长为征途霸主。',
    keywords: '征途国际攻略,征途国际新手攻略,征途国际职业攻略,征途国际装备攻略,征途国际国战攻略',
  },
  news: {
    title: '征途国际新闻资讯 - 最新动态与官方公告 | 征途国际官方下载',
    description: '征途国际最新新闻资讯：官方公告、游戏动态、开发计划、赛事报道等一手信息，第一时间了解征途国际最新消息。',
    keywords: '征途国际新闻,征途国际公告,征途国际资讯,征途国际动态,征途国际赛事',
  },
  updates: {
    title: '征途国际版本更新 - 更新日志与内容详解 | 征途国际官方下载',
    description: '征途国际版本更新日志：每次版本更新的详细内容、新增功能、系统优化、平衡调整等完整记录，不再错过任何重要更新。',
    keywords: '征途国际更新,征途国际版本,征途国际更新日志,征途国际新版本,征途国际最新版',
  },
  events: {
    title: '征途国际活动福利 - 限时活动与福利领取攻略 | 征途国际官方下载',
    description: '征途国际最新活动福利汇总：限时活动、登录奖励、节日福利、签到好礼等全部活动信息，不错过任何福利。',
    keywords: '征途国际活动,征途国际福利,征途国际礼包,征途国际限时活动,征途国际奖励',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || 'strategy');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`, { replace: true });
  };

  const filteredArticles = articles.filter((a) => a.category === activeCategory);
  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const meta = categoryMeta[activeCategory] || categoryMeta.strategy;
  const categoryName = currentCategory?.name || '游戏攻略';

  return (
    <>
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://mobilestart.github.io/category/${activeCategory}`}
      />

      <div className="pt-24 pb-16 md:pb-24 bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-7xl">
          <div className="mb-6">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              {categoryName}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base">
              {currentCategory?.description || ''}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group rounded-2xl bg-background-100 overflow-hidden hover:bg-background-200/70 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      <strong>{article.title}</strong>
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无内容</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}